import SessionService from '../features/session/data/session.service'
import { useAppStore } from '../shared/store/app.store.ts'
import { mapProjectDtoToProject } from '../features/session/domain/mappers/project.mapper'
import { mapSessionDtoToSession } from '../features/session/domain/mappers/session.mapper'
import { loadAudioForm } from '@/hooks/useLoadAudioForm.ts'
import { useScriptStore } from '@/shared/store/script.store.ts'
import type { Session } from '@/features/session/domain/models/session.ts'
import { createSession } from '@/features/session/domain/use-cases/create-session.ts'
import { useSpeechStore } from '@/shared/store/speech.store.ts'
import { loadSession as loadSessionUseCase } from '@/features/session/domain/use-cases/load-session.ts'
import { shouldRebuildTimelineFromAudioform } from '@/features/session/domain/use-cases/should-rebuild-timeline-from-audioform.ts'
import { isDeepEqual } from '@/utils/is-deep-equal'
import { WorkflowId } from '@/models'
import { resolveSessionAudioformId } from '@/features/session/domain/use-cases/resolve-session-audioform-id.ts'

export const loadSession = async (
  projectId: string,
  sessionId: string,
  adId?: string
): Promise<void> => {
  const { setProject } = useAppStore.getState()

  try {
    const [projectDto, sessionDto] = await Promise.all([
      SessionService.getProject(projectId),
      SessionService.getSession(projectId, sessionId),
    ])

    const project = mapProjectDtoToProject(projectDto)

    setProject(project)
    const audioformId = resolveSessionAudioformId(sessionDto, adId)

    const session = await getOrCreateEditorSession(
      project.id,
      mapSessionDtoToSession(sessionDto),
      audioformId
    )
    // If workflowId is "Editor" and has state, load from saved session state.
    // When the saved timeline was wiped but an audioform still exists, rebuild
    // the mix from the audioform while keeping scripts/speeches from state.
    if (
      sessionDto.workflowId === (WorkflowId.PRO_EDITOR as string) &&
      !isDeepEqual(sessionDto.state ?? {}, {})
    ) {
      if (shouldRebuildTimelineFromAudioform(sessionDto) && audioformId) {
        loadScripts(session)
        loadSpeeches(session)
        await loadAudioForm(audioformId)
      } else {
        await loadSessionUseCase(sessionDto)
      }
    } else {
      loadScripts(session)
      loadSpeeches(session)
      if (audioformId) {
        await loadAudioForm(audioformId)
      }
    }
  } catch {
    console.error('session could not be loaded')
    // todo: show toast
  }
}

const getOrCreateEditorSession = async (
  projectId: string,
  currentSession: Session,
  audioformId?: string
): Promise<Session> => {
  const { setSession } = useAppStore.getState()

  const editorSession =
    currentSession.workflowId === (WorkflowId.PRO_EDITOR as string)
      ? currentSession
      : await createSession(audioformId, projectId)

  setSession(editorSession)
  return mapSessionDtoToSession(editorSession)
}

const loadScripts = (session: Session): void => {
  const { storeScript } = useScriptStore.getState()
  session.state.scripts?.forEach(({ id, text, lastUpdate }) => {
    storeScript({ id, text, lastUpdate: new Date(lastUpdate) })
  })
}

const loadSpeeches = (session: Session): void => {
  const { storeSpeeches } = useSpeechStore.getState()
  storeSpeeches(session.state.speeches ?? {})
}
